
import React, { useRef, useState } from 'react';
import { RenovationProject, RenovationTask, TaskStatus } from '../../types';
import { LayoutList, Calendar } from 'lucide-react';
import { motion } from 'framer-motion';

interface GanttChartProps {
  project: RenovationProject;
  onUpdateTask: (task: RenovationTask) => void;
}

const GanttChart: React.FC<GanttChartProps> = ({ project, onUpdateTask }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const [draggingId, setDraggingId] = useState<string | null>(null);

  const tasks = project.tasks.filter(t => t.startDate && t.endDate);

  // Calculate timeline bounds
  const dates = tasks.flatMap(t => [new Date(t.startDate!).getTime(), new Date(t.endDate!).getTime()]);
  const minDate = dates.length ? Math.min(...dates) : Date.now();
  const maxDate = dates.length ? Math.max(...dates) : Date.now() + 86400000 * 30; // default 30 days
  const totalDuration = maxDate - minDate + (86400000 * 5); // Add buffer

  // Scale: 1 day = X pixels
  const pxPerDay = 40;
  const totalDays = Math.ceil(totalDuration / 86400000);
  const chartWidth = totalDays * pxPerDay;

  const getX = (dateStr: string) => {
    const d = new Date(dateStr).getTime();
    return ((d - minDate) / 86400000) * pxPerDay;
  };

  const handleDragEnd = (taskId: string, info: any) => {
    const task = tasks.find(t => t.id === taskId);
    if (!task) return;

    const deltaPixels = info.offset.x;
    const deltaDays = Math.round(deltaPixels / pxPerDay);

    if (deltaDays !== 0) {
      const start = new Date(task.startDate!);
      const end = new Date(task.endDate!);
      start.setDate(start.getDate() + deltaDays);
      end.setDate(end.getDate() + deltaDays);

      onUpdateTask({
        ...task,
        startDate: start.toISOString().split('T')[0],
        endDate: end.toISOString().split('T')[0]
      });
    }
    setDraggingId(null);
  };

  if (tasks.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-slate-500 border border-dashed border-slate-800 rounded-3xl bg-slate-900/20">
        <Calendar className="w-16 h-16 mb-4 opacity-20" />
        <p>No scheduled tasks found.</p>
        <p className="text-sm opacity-50">Assign start and end dates to tasks to see the timeline.</p>
      </div>
    );
  }

  return (
    <div className="space-y-4 h-full flex flex-col">
      <div className="flex justify-between items-center">
        <h3 className="text-xl font-bold text-white flex items-center gap-2">
          <LayoutList className="w-5 h-5 text-emerald-500" /> Interactive Schedule
        </h3>
        <div className="text-xs text-slate-500 flex gap-4">
          <div className="flex items-center gap-2"><div className="w-3 h-3 bg-blue-500 rounded"></div> In Progress</div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 bg-emerald-500 rounded"></div> Completed</div>
          <div className="flex items-center gap-2"><div className="w-3 h-3 bg-slate-600 rounded"></div> Planned</div>
          <span className="italic ml-4 hidden md:inline">Drag bars to reschedule</span>
        </div>
      </div>

      <div className="flex-1 bg-slate-900/50 rounded-2xl border border-slate-800 p-6 overflow-hidden relative flex flex-col">
        {/* Header Days */}
        <div className="flex border-b border-slate-700 pb-2 mb-4 overflow-hidden" style={{ width: '100%' }}>
          <div className="w-48 shrink-0 border-r border-slate-800 pr-4 font-bold text-slate-500 text-xs uppercase pt-2">Task Name</div>
          <div className="flex-1 overflow-x-auto custom-scrollbar" ref={containerRef}>
            <div className="relative h-8" style={{ width: chartWidth }}>
              {Array.from({ length: totalDays }).map((_, i) => {
                const d = new Date(minDate + i * 86400000);
                return (i % 7 === 0) && (
                  <div key={i} className="absolute text-[10px] text-slate-600 border-l border-slate-800 pl-1 h-full" style={{ left: i * pxPerDay }}>
                    {d.getDate()}/{d.getMonth() + 1}
                  </div>
                )
              })}
            </div>

            {/* Bars Container */}
            <div className="relative space-y-3 pb-4" style={{ width: chartWidth }}>
              {tasks.map((task) => {
                const startX = getX(task.startDate!);
                const endX = getX(task.endDate!);
                const width = Math.max(endX - startX, pxPerDay); // Min 1 day width

                let colorClass = 'bg-slate-600';
                if (task.status === TaskStatus.InProgress) colorClass = 'bg-blue-500';
                if (task.status === TaskStatus.Completed) colorClass = 'bg-emerald-500';

                return (
                  <div key={task.id} className="relative h-10 flex items-center">
                    {/* Grid Lines */}
                    <div className="absolute inset-0 border-b border-slate-800/30 w-full"></div>

                    <motion.div
                      drag="x"
                      dragMomentum={false}
                      dragConstraints={containerRef}
                      onDragStart={() => setDraggingId(task.id)}
                      onDragEnd={(e, info) => handleDragEnd(task.id, info)}
                      className={`absolute h-6 rounded-md ${colorClass} shadow-lg cursor-grab active:cursor-grabbing flex items-center px-2 z-10 hover:brightness-110`}
                      style={{ left: startX, width }}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <span className="text-[10px] font-bold text-white truncate drop-shadow-md sticky left-2">
                        {task.title}
                      </span>
                    </motion.div>
                  </div>
                );
              })}

              {/* Today Marker */}
              <div
                className="absolute top-0 bottom-0 border-l-2 border-red-500/50 z-0 pointer-events-none"
                style={{ left: ((Date.now() - minDate) / 86400000) * pxPerDay }}
              >
                <div className="text-[9px] text-red-400 bg-red-500/10 px-1 ml-1 mt-1 rounded w-max">Today</div>
              </div>
            </div>
          </div>
        </div>

        {/* Sidebar Labels Overlay (Sticky) */}
        <div className="absolute top-[86px] left-6 w-48 space-y-3 pointer-events-none">
          {tasks.map(task => (
            <div key={task.id} className="h-10 flex items-center text-xs font-medium text-slate-400 truncate pr-4">
              {task.title}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default GanttChart;
